import { useLayoutEffect, useState, type RefObject } from "react";

/* 193 §D.0 — THE WINDOW'S MEASURE, MOVED OUT OF TimeEntryTable AND NOT
   CHANGED. What stood inline at TimeEntryTable.tsx:245-283 (151D §1(f)+(h) /
   §2(c)(e): "THE WINDOW IS SIZED BY MEASUREMENT") is this function, line for
   line — `windowed` became `enabled`, `teWin.ref` the `win` ref, `tableRef`
   the `table` ref, the `.csSec-time` selector a parameter, `canSeeCounters`
   the `attachKey` the effect re-runs on — so that a second windowed table
   (the pro forma list, 193 §D) sizes itself by the same measurement rather
   than by a copy of it. Extract, don't improve (SESSION_RULES rule 3): the
   arithmetic, the 240 floor, the observers and the zoom division are the
   te's own; the comment that explains them stays at the te (it is about the
   te's page), and this file says only what the parameters mean.

   The window gets `scroller.clientHeight − tail − overhead`: `scroller` is
   the nearest vertically scrolling ancestor of the window (else the
   document); `sec` is the window's section — `win.parentElement.closest(
   sectionSelector)`, or the parent itself when no selector is given or none
   matches; `overhead` is the section's own rows above and below the window
   (sec minus win, both offsetHeight); `tail` is everything the scroller
   holds BELOW the section. offsetHeight/scrollTop/clientHeight are LAYOUT px;
   the one rect subtraction is visual and divided by the root zoom whole (142
   §1). `hintInsets.up` is the pinned header's height, so the up arrow sits
   under it; `down` is 0 since 190 round 8 [AK]. */
export function useWindowMax(opts: {
  enabled: boolean;
  win: RefObject<HTMLElement | null>;
  table: RefObject<HTMLTableElement | null>;
  sectionSelector?: string;
  attachKey?: unknown;
}): { winMax: number | null; hintInsets: { up: number; down: number } } {
  const { enabled, win: winRef, table: tableRef, sectionSelector, attachKey } = opts;
  const [winMax, setWinMax] = useState<number | null>(null);
  const [hintInsets, setHintInsets] = useState<{ up: number; down: number }>({ up: 0, down: 0 });
  useLayoutEffect(() => {
    if (!enabled) {
      // the te's own line: an unwindowed mount clears the measured max
      setWinMax(null);
      return;
    }
    const win = winRef.current;
    const root = win?.parentElement;
    if (!win || !root) return;
    const sec = (sectionSelector ? root.closest<HTMLElement>(sectionSelector) : null) ?? root;
    let found: HTMLElement | null = win.parentElement;
    while (found && found !== document.body) {
      const oy = getComputedStyle(found).overflowY;
      if (oy === "auto" || oy === "scroll") break;
      found = found.parentElement;
    }
    const scroller: HTMLElement =
      !found || found === document.body
        ? (document.scrollingElement as HTMLElement | null) ?? document.documentElement
        : found;
    const compute = () => {
      const zoom = parseFloat(getComputedStyle(document.documentElement).zoom || "1") || 1;
      const secBottomInContent =
        (sec.getBoundingClientRect().bottom - scroller.getBoundingClientRect().top) / zoom + scroller.scrollTop;
      const tail = Math.max(0, scroller.scrollHeight - secBottomInContent);
      const overhead = sec.offsetHeight - win.offsetHeight;
      const max = Math.max(240, Math.floor(scroller.clientHeight - tail - overhead));
      const thead = tableRef.current ? tableRef.current.tHead : null;
      const up = thead ? thead.offsetHeight : 0;
      const down = 0; // 190 round 8 [AK] — the plate left the window
      setWinMax((p) => (p === max ? p : max));
      setHintInsets((p) => (p.up === up && p.down === down ? p : { up, down }));
    };
    compute();
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(compute);
      ro.observe(sec);
      ro.observe(scroller);
      const th = tableRef.current ? tableRef.current.tHead : null;
      if (th) ro.observe(th);
    }
    window.addEventListener("resize", compute);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", compute);
    };
    // The te's own dependency list — the window predicate and the key its host
    // re-measures on (the counters' visibility, which moves the scroller's
    // clientHeight) — plus the two refs and the selector, each stable for a
    // mount, named so the rule of hooks is satisfied without a disable.
  }, [enabled, attachKey, winRef, tableRef, sectionSelector]);
  return { winMax, hintInsets };
}
